/**
 * `<ScreenBuilder>` — a screen in three steps: conditions, columns, sort and
 * name (owner, 2026-10-26; mock: `docs/screener-builder-mock.html`). A new
 * screen gets a **Start** step in front when the caller is offered a preset
 * screen (`lib/screen-presets.ts`; owner, 2026-10-29) — Blank, or a preset that
 * fills the other three, all of it still editable. Edit never shows it.
 *
 * One search box per step. It finds presets, the screen's own conditions and
 * every column as you type; when none fits, the last row is **✦ Describe**,
 * which turns the words into ScanScript you read before adding. The draft is on
 * the right the whole time and is written once, on Create or Save
 * (`lib/screen-builder.ts`).
 *
 * New and Edit are the same dialog seeded differently, so they cannot drift.
 * The island renders and reports; the host creates or patches the screen,
 * because only the host knows screens exist.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { ScreenerConfig, ScreenerExpr, ScreenerSort } from "@qufolio/model";

import { COLUMN_SETS } from "../lib/column-sets.js";
import { pickerRows, searchRows, shownFields, withSuggested } from "../lib/column-picker.js";
import { offeredPresets, presetAdded, type ConditionPreset, type PresetGate } from "../lib/condition-presets.js";
import type { UniverseColumn } from "../lib/column-universe.js";
import type { DescribeColumn, DescribeExpr } from "../lib/compile.js";
import {
  addDescribed,
  addDescribedColumn,
  addPreset,
  draftFrom,
  finish,
  showColumn,
  startFrom,
  tick,
  type BuilderDraft,
  type BuilderResult,
} from "../lib/screen-builder.js";
import { offeredScreenPresets, screenPresetConditions, type ScreenPreset } from "../lib/screen-presets.js";
import { ColumnPicker } from "./ColumnPicker.js";
import { ConditionPresetList } from "./ConditionPresetList.js";

export interface ScreenBuilderProps {
  mode: "new" | "edit";
  /** The screen being edited, or the config a new one starts from. */
  config: ScreenerConfig;
  /** Every column the screen could show (`buildUniverse`). */
  universe: readonly UniverseColumn[];
  /** The screen's name today (Edit), or the one a new screen is offered. */
  name: string;
  /** "SP500" — said, not chosen: the universe is the screener bar's. */
  universeLabel?: string;
  /** The builder's own describer (`/dsl/describe/builder`). Absent: no ✦ row. */
  describe?: DescribeExpr;
  /** What the describer is told the columns mean. */
  describeColumns?: DescribeColumn[];
  mintId: () => string;
  onDone: (result: BuilderResult) => void;
  onClose: () => void;
  /** Edit only: the full editor, for formats, widths and expressions. */
  onFullEditor?: () => void;
  /** Which gated presets — conditions and screens — this caller is offered. */
  presetGate?: PresetGate;
  /** Every screen's name, so a preset's copy gets a free one ("Pre Movers 2"). */
  takenNames?: readonly string[];
}

type Step = "start" | "conditions" | "columns" | "finish";
const START: { id: Step; label: string } = { id: "start", label: "Start" };
const STEPS: { id: Step; label: string }[] = [
  { id: "conditions", label: "Conditions" },
  { id: "columns", label: "Columns" },
  { id: "finish", label: "Sort & name" },
];

type Described =
  | { words: string; state: "pending" }
  | { words: string; state: "error"; message: string }
  | { words: string; state: "ok"; expression: ScreenerExpr; source: string };

export function ScreenBuilder({
  mode,
  config,
  universe,
  name,
  universeLabel,
  describe,
  describeColumns,
  mintId,
  onDone,
  onClose,
  onFullEditor,
  presetGate,
  takenNames = [],
}: ScreenBuilderProps) {
  // Offered once: a gate that changed under an open dialog would move its steps.
  const [screenPresets] = useState<ScreenPreset[]>(() =>
    mode === "new" ? offeredScreenPresets(presetGate) : [],
  );
  const steps = screenPresets.length > 0 ? [START, ...STEPS] : STEPS;
  const [step, setStep] = useState<Step>(steps[0]!.id);
  const [blank] = useState<BuilderDraft>(() => draftFrom(config, name, shownFields(universe)));
  const [draft, setDraft] = useState<BuilderDraft>(blank);
  const [startedFrom, setStartedFrom] = useState<string>("blank");
  const [query, setQuery] = useState<Record<Step, string>>({ start: "", conditions: "", columns: "", finish: "" });
  const [described, setDescribed] = useState<Described | null>(null);
  const [browsing, setBrowsing] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !browsing) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, browsing]);

  const offered = useMemo(() => withSuggested(universe), [universe]);
  const headerOf = useMemo(() => {
    const map = new Map(offered.map((u) => [u.field, u.header]));
    for (const c of draft.baseColumns) if (c.header) map.set(c.field, c.header);
    return (field: string) => map.get(field) ?? field;
  }, [offered, draft.baseColumns]);

  const q = query[step].trim();
  const at = steps.findIndex((s) => s.id === step);

  const runDescribe = async (words: string) => {
    if (!describe) return;
    setDescribed({ words, state: "pending" });
    const result = await describe(words, describeColumns);
    setDescribed(
      result.ok
        ? { words, state: "ok", expression: result.expression, source: result.source }
        : { words, state: "error", message: result.error.message },
    );
  };

  const describeRow = (kind: "condition" | "column") => {
    if (!describe || !q) return null;
    const mine = described && described.words === q ? described : null;
    if (!mine) {
      return (
        <>
          <div style={sect}>Not a preset</div>
          <button type="button" onClick={() => void runDescribe(q)} style={{ ...rowStyle, ...describeButton }}>
            <span style={{ flex: 1, textAlign: "left" }}>
              <span style={aiText}>✦ Describe “{q}”</span>
              <span style={sub}>Turns it into ScanScript. You see it before it is added.</span>
            </span>
          </button>
        </>
      );
    }
    return (
      <div style={aiBox}>
        {mine.state === "pending" && <span style={aiText}>✦ Describing “{q}”…</span>}
        {mine.state === "error" && <span style={{ color: "var(--qf-negative)" }}>{mine.message}</span>}
        {mine.state === "ok" && (
          <>
            <span style={aiText}>✦ “{q}”</span>
            <code style={codeBlock}>{mine.source}</code>
            <button
              type="button"
              style={addButton}
              onClick={() => {
                setDraft((d) =>
                  kind === "condition"
                    ? addDescribed(d, q, mine.expression, mintId())
                    : addDescribedColumn(d, q, mine.expression, mine.source),
                );
                setDescribed(null);
                setQuery((qs) => ({ ...qs, [step]: "" }));
              }}
            >
              + Add as {kind}
            </button>
          </>
        )}
      </div>
    );
  };

  const startStep = () => {
    const needle = q.toLowerCase();
    const shown = screenPresets.filter((p) => !needle || `${p.name} ${p.blurb}`.toLowerCase().includes(needle));
    const picked = (id: string) => (startedFrom === id ? { borderColor: "var(--qf-accent)" } : {});
    return (
      <>
        <div style={sub}>Start blank, or from a preset — everything stays editable.</div>
        <div style={{ ...cards, marginTop: "0.5rem" }}>
          {!needle && (
            <button
              type="button"
              onClick={() => {
                setDraft(blank);
                setStartedFrom("blank");
                setStep("conditions");
              }}
              style={{ ...card, ...picked("blank") }}
            >
              <b>Blank</b>
              <span style={sub}>Symbol, Last, Chg % only</span>
            </button>
          )}
          {shown.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setDraft(startFrom(blank, p, screenPresetConditions(p), mintId, takenNames));
                setStartedFrom(p.id);
                setStep("conditions");
              }}
              style={{ ...card, ...picked(p.id) }}
            >
              <b>{p.name}</b>
              <span style={sub}>{p.blurb}</span>
              <span style={sub}>{p.columns.filter((c) => c.field !== "logo").length} columns</span>
            </button>
          ))}
        </div>
      </>
    );
  };

  const conditionsStep = () => {
    const needle = q.toLowerCase();
    const mine = draft.library.filter(
      (f) => !needle || `${f.name} ${f.category ?? ""}`.toLowerCase().includes(needle),
    );
    const presets: ConditionPreset[] = needle
      ? offeredPresets(presetGate).filter((p) => `${p.name} ${p.category} ${p.source}`.toLowerCase().includes(needle))
      : [];
    return (
      <>
        {mine.length > 0 && <div style={sect}>This screen's conditions</div>}
        {mine.map((f) => (
          <label key={f.id} style={rowStyle}>
            <input
              type="checkbox"
              checked={draft.ticked.includes(f.id)}
              onChange={(e) => setDraft((d) => tick(d, f.id, e.target.checked))}
            />
            <span style={{ flex: 1, minWidth: 0 }}>
              {f.name}
              {f.category && <span style={sub}>{f.category}</span>}
            </span>
          </label>
        ))}
        {needle ? (
          presets.length > 0 && (
            <>
              <div style={sect}>Presets</div>
              {presets.map((p) => {
                const added = presetAdded(draft.library, p);
                return (
                  <div key={p.id} style={rowStyle} title={p.source}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {p.name}
                      <span style={sub}>{p.category}</span>
                    </span>
                    <button
                      type="button"
                      disabled={added}
                      aria-label={added ? `${p.name} added` : `Add ${p.name}`}
                      onClick={() => setDraft((d) => addPreset(d, p, mintId()))}
                      style={{ ...addButton, ...(added ? addedButton : {}) }}
                    >
                      {added ? "✓ Added" : "+ Add"}
                    </button>
                  </div>
                );
              })}
            </>
          )
        ) : (
          <>
            <div style={sect}>Presets</div>
            <ConditionPresetList
              filters={draft.library}
              onAdd={(p) => setDraft((d) => addPreset(d, p, mintId()))}
              allowed={presetGate}
            />
          </>
        )}
        {describeRow("condition")}
      </>
    );
  };

  const columnsStep = () => {
    const rows = q ? searchRows(pickerRows(offered), q) : [];
    return (
      <>
        {!q && (
          <>
            <div style={sect}>Column presets</div>
            <div style={cards}>
              {COLUMN_SETS.map((set) => {
                const present = set.fields.filter((f) => offered.some((u) => u.field === f));
                return (
                  <button
                    key={set.id}
                    type="button"
                    disabled={present.length === 0}
                    onClick={() =>
                      setDraft((d) => present.reduce((acc, f) => showColumn(acc, f, true), d))
                    }
                    style={card}
                  >
                    <b>{set.label}</b>
                    <span style={sub}>+ {present.length} columns</span>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: "0.6rem" }}>
              <button type="button" onClick={() => setBrowsing(true)} style={linkButton}>
                Browse every column…
              </button>
            </div>
          </>
        )}
        {rows.map((row) => {
          const field = row.variants[0]!.field;
          const on = row.variants.some((v) => draft.shown.includes(v.field));
          return (
            <div key={row.key} style={rowStyle}>
              <span style={{ flex: 1, minWidth: 0 }}>
                {row.header}
                <span style={sub}>{row.description || row.topic}</span>
              </span>
              <button
                type="button"
                aria-label={on ? `${row.header} added` : `Add ${row.header}`}
                disabled={on}
                onClick={() => setDraft((d) => showColumn(d, field, true))}
                style={{ ...addButton, ...(on ? addedButton : {}) }}
              >
                {on ? "✓ Added" : "+ Add"}
              </button>
            </div>
          );
        })}
        {describeRow("column")}
      </>
    );
  };

  const finishStep = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", paddingTop: "0.4rem" }}>
      <label style={field}>
        <span style={fieldLabel}>Name</span>
        <input
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          aria-label="Screen name"
          style={input}
        />
      </label>
      <label style={field}>
        <span style={fieldLabel}>Sort by</span>
        <select
          aria-label="Sort by"
          value={draft.sort ? `${draft.sort.field}|${draft.sort.dir}` : ""}
          onChange={(e) => {
            const [f, dir] = e.target.value.split("|");
            setDraft((d) => ({
              ...d,
              sort: f ? ({ field: f, dir: dir as ScreenerSort["dir"] } as ScreenerSort) : null,
            }));
          }}
          style={input}
        >
          <option value="">No sort</option>
          {draft.shown.flatMap((f) => [
            <option key={`${f}|desc`} value={`${f}|desc`}>{`${headerOf(f)} ↓`}</option>,
            <option key={`${f}|asc`} value={`${f}|asc`}>{`${headerOf(f)} ↑`}</option>,
          ])}
        </select>
      </label>
      {universeLabel && (
        <div style={sub}>
          Universe: <b style={{ color: "var(--qf-text)" }}>{universeLabel}</b> — change it on the screener bar any time.
        </div>
      )}
    </div>
  );

  const ticked = draft.library.filter((f) => draft.ticked.includes(f.id));

  return (
    <div style={backdrop} onClick={onClose}>
      <div role="dialog" aria-label={mode === "new" ? "New screen" : "Edit screen"} style={dialog} onClick={(e) => e.stopPropagation()}>
        <header style={head}>
          <b>{mode === "new" ? "New screen" : "Edit screen"}</b>
          <span style={tabs}>
            {steps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStep(s.id)}
                style={{
                  ...tabButton,
                  background: step === s.id ? "var(--qf-surface-alt)" : "transparent",
                  color: step === s.id ? "var(--qf-text)" : "var(--qf-text-muted)",
                }}
              >
                {i + 1} {s.label}
              </button>
            ))}
          </span>
          <span style={{ flex: 1 }} />
          {mode === "edit" && onFullEditor && (
            <button type="button" onClick={onFullEditor} style={linkButton}>
              Open the full editor
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close" style={iconButton}>
            ✕
          </button>
        </header>

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {step !== "finish" && (
              <input
                autoFocus
                value={query[step]}
                onChange={(e) => {
                  const value = e.target.value;
                  setQuery((qs) => ({ ...qs, [step]: value }));
                }}
                placeholder={
                  step === "start"
                    ? "Search preset screens — e.g. movers"
                    : step === "conditions"
                    ? describe
                      ? "Search conditions, or describe one — e.g. “rsi below 30”"
                      : "Search conditions"
                    : describe
                      ? "Search columns by name or meaning, or describe one"
                      : "Search columns by name or meaning"
                }
                aria-label="Search"
                style={{ ...input, margin: "0.75rem 0.75rem 0.4rem" }}
              />
            )}
            <div style={{ flex: 1, overflowY: "auto", padding: "0 0.75rem 0.75rem" }}>
              {step === "start" && startStep()}
              {step === "conditions" && conditionsStep()}
              {step === "columns" && columnsStep()}
              {step === "finish" && finishStep()}
            </div>
          </div>

          <aside style={side}>
            <div style={{ fontWeight: 600, marginBottom: "0.4rem" }}>{draft.name || "New screen"}</div>
            <div style={sideHead}>
              <span style={{ flex: 1 }}>Conditions — all must match</span>
              {ticked.length}
            </div>
            {ticked.length === 0 && <div style={sub}>None yet: every row passes.</div>}
            {ticked.map((f) => (
              <div key={f.id} style={sideRow}>
                <span style={{ flex: 1, minWidth: 0 }}>{f.name}</span>
                <button type="button" aria-label={`Untick ${f.name}`} onClick={() => setDraft((d) => tick(d, f.id, false))} style={iconButton}>
                  ✕
                </button>
              </div>
            ))}
            <div style={sideHead}>
              <span style={{ flex: 1 }}>Columns</span>
              {draft.shown.length}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {draft.shown.map((f) => (
                <span key={f} style={chip}>
                  {headerOf(f)}
                  <button type="button" aria-label={`Remove ${headerOf(f)}`} onClick={() => setDraft((d) => showColumn(d, f, false))} style={chipX}>
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <div style={sideHead}>
              <span style={{ flex: 1 }}>Sort</span>
            </div>
            <div style={sub}>{draft.sort ? `${headerOf(draft.sort.field)} ${draft.sort.dir === "desc" ? "↓" : "↑"}` : "None"}</div>
          </aside>
        </div>

        <footer style={foot}>
          <span style={sub}>
            Step {at + 1} of {steps.length}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" disabled={at === 0} onClick={() => setStep(steps[at - 1]!.id)} style={button}>
            Back
          </button>
          {at < steps.length - 1 && (
            <button type="button" onClick={() => setStep(steps[at + 1]!.id)} style={button}>
              Next
            </button>
          )}
          <button type="button" onClick={() => onDone(finish(draft, config))} style={{ ...button, ...primary }}>
            {mode === "new" ? "Create screen" : "Save"}
          </button>
        </footer>
      </div>

      {browsing && (
        <div onClick={(e) => e.stopPropagation()}>
          <ColumnPicker
            universe={offered.map((u) => ({ ...u, visible: draft.shown.includes(u.field) }))}
            columns={draft.baseColumns}
            initialTab="browse"
            onChange={(next) =>
              setDraft((d) => ({ ...d, shown: next.filter((c) => !c.hide).map((c) => c.field) }))
            }
            onClose={() => setBrowsing(false)}
          />
        </div>
      )}
    </div>
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const dialog: CSSProperties = {
  width: "min(1000px, calc(100vw - 16px))",
  height: "min(700px, calc(100vh - 32px))",
  display: "flex",
  flexDirection: "column",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 8,
  boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
  color: "var(--qf-text)",
  fontSize: "0.8rem",
};
const head: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  padding: "0.6rem 0.75rem",
  borderBottom: "1px solid var(--qf-border)",
  flexWrap: "wrap",
};
const tabs: CSSProperties = { display: "inline-flex", gap: 2, padding: 2, borderRadius: 5, background: "var(--qf-background)" };
const tabButton: CSSProperties = { border: "none", borderRadius: 4, padding: "0.25rem 0.75rem", cursor: "pointer", fontSize: "0.8rem" };
const input: CSSProperties = {
  padding: "0.35rem 0.5rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.82rem",
};
const sect: CSSProperties = {
  padding: "0.5rem 0.2rem 0.2rem",
  marginTop: "0.3rem",
  borderTop: "1px solid var(--qf-border)",
  color: "var(--qf-text-secondary)",
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};
const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.3rem 0.4rem",
  borderRadius: 4,
  cursor: "pointer",
  width: "100%",
  background: "transparent",
  border: "none",
  color: "var(--qf-text)",
  fontSize: "0.8rem",
};
const sub: CSSProperties = { display: "block", color: "var(--qf-text-muted)", fontSize: "0.7rem" };
const aiText: CSSProperties = { color: "var(--qf-accent-purple)" };
const describeButton: CSSProperties = { border: "1px dashed var(--qf-accent-purple)" };
const aiBox: CSSProperties = {
  marginTop: "0.5rem",
  padding: "0.5rem 0.6rem",
  border: "1px solid var(--qf-accent-purple)",
  borderRadius: 6,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "0.35rem",
};
const codeBlock: CSSProperties = {
  display: "block",
  width: "100%",
  padding: "0.35rem 0.5rem",
  background: "var(--qf-background)",
  borderRadius: 4,
  fontSize: "0.75rem",
};
const cards: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.5rem" };
const card: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 2,
  padding: "0.5rem 0.6rem",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  cursor: "pointer",
  fontSize: "0.8rem",
  textAlign: "left",
};
const side: CSSProperties = {
  width: 280,
  flex: "0 0 auto",
  borderLeft: "1px solid var(--qf-border)",
  padding: "0.75rem",
  overflowY: "auto",
  background: "var(--qf-surface)",
};
const sideHead: CSSProperties = {
  display: "flex",
  margin: "0.7rem 0 0.3rem",
  color: "var(--qf-text-secondary)",
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};
const sideRow: CSSProperties = { display: "flex", alignItems: "center", gap: 4, padding: "0.12rem 0" };
const chip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  padding: "0 0.35rem",
  borderRadius: 3,
  background: "var(--qf-selection)",
  fontSize: "0.72rem",
};
const chipX: CSSProperties = { border: "none", background: "none", color: "var(--qf-text-muted)", cursor: "pointer", padding: 0, fontSize: "0.68rem" };
const field: CSSProperties = { display: "flex", alignItems: "center", gap: "0.6rem" };
const fieldLabel: CSSProperties = { width: 60, color: "var(--qf-text-secondary)" };
const foot: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.6rem 0.75rem",
  borderTop: "1px solid var(--qf-border)",
};
const button: CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.3rem 0.9rem",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  cursor: "pointer",
  fontSize: "0.8rem",
};
const primary: CSSProperties = { background: "var(--qf-accent)", borderColor: "var(--qf-accent)", color: "var(--qf-background)" };
const addButton: CSSProperties = {
  flex: "0 0 auto",
  border: "1px solid var(--qf-accent)",
  borderRadius: 4,
  padding: "0 0.4rem",
  background: "transparent",
  color: "var(--qf-accent)",
  fontSize: "0.7rem",
  cursor: "pointer",
};
const addedButton: CSSProperties = { borderColor: "var(--qf-positive)", color: "var(--qf-positive)", cursor: "default" };
const iconButton: CSSProperties = { border: "none", background: "transparent", color: "var(--qf-text-muted)", cursor: "pointer", fontSize: "0.8rem", padding: "0.1rem 0.3rem" };
const linkButton: CSSProperties = { border: "none", background: "transparent", color: "var(--qf-accent)", cursor: "pointer", fontSize: "0.75rem", padding: 0 };
